import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import {
  BY_THE_SYSTEM,
  DERIVED_FORUM_NOTE_PREFIX,
  FROM,
  type GrantActor,
  type GrantLevel,
  LEGACY_LINK,
  LEGACY_LINK_EXPLANATION,
  type GrantLockReason,
  type GrantLockWording,
  NOT_GRANTED,
  UNRECOGNIZED_PARENT,
  derivedForumSource,
  grantedBy,
  grantedOn,
  levelHelp,
  levelLabel,
  lockWording,
  parentLabel,
  removedOn,
} from "permissions/_views/wording/permission-wording";
import { fromTemplate } from "permissions/_views/wording/permission-wording";
import { templatePath } from "permissions/_views/templates/templates-paths";
import { type NameState, personStateText } from "permissions/_views/targets/permission-target-state";
import type Permission from "permissions/permission";
import type { Resource } from "permissions/resources/permission-resource";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { LoginAccessType } from "permissions/resources/users/login-access-type";

/**
 * What one grant row shows, worked out from a kind of grant and the grant itself (or its absence).
 *
 * Kept apart from the component so the rules — which controls a kind has, when a row is locked and
 * why, what its source says — are plain functions a test can read without rendering. The component
 * draws whatever this returns and decides nothing.
 */

export type GrantRowState = "enabled" | "removed" | "not-granted";

/** One choice in the level control: a (level, scope) pair the kind supports. */
export type GrantLevelOption = Readonly<{
  level: GrantLevel;
  label: string;
  help?: string;
}>;

/** A parent a derived grant came from, linked to the parent's own row where the page draws it. */
export type GrantParent = Readonly<{ label: string; rowId?: string }>;

export type GrantSource =
  | Readonly<{ type: "grantor"; label: string }>
  /**
   * A grantor the directory has not named: still loading, gone, hidden from the viewer, or a lookup
   * that failed. The label says when; `stateText` says why there is no name, beneath it.
   */
  | Readonly<{ type: "unnamed-grantor"; state: Exclude<NameState["state"], "found">; label: string; stateText?: string }>
  | Readonly<{ type: "system"; label: string; note?: string }>
  | Readonly<{ type: "parents"; parents: readonly GrantParent[] }>
  | Readonly<{ type: "roster"; label: string }>
  /** A grant a template application created or raised. `href` is the template's page; none once it is deleted. */
  | Readonly<{ type: "template"; label: string; href?: string }>
  /** A document grant linked to a parent that documents no longer read: listed only to be removed. */
  | Readonly<{ type: "legacy"; label: string; explanation: string }>;

export type GrantLock = Readonly<{ reason: GrantLockReason } & GrantLockWording>;

export type GrantRowModel = Readonly<{
  /** The DOM id of the row, so a derived row's "From:" link can point at its parent. */
  rowId: string;
  kind: PermissionKind;
  state: GrantRowState;
  /** The level the grant holds, or for a row not granted, what Grant would send. */
  level: GrantLevel;
  /** The level chip's text, or "Not granted". */
  levelText: string;
  /** Which control the level chip stands for, which names it for a screen reader. */
  levelControl?: "accessLevel" | "scope";
  /** The level control's choices. Empty where the level cannot be changed from this row. */
  levelOptions: readonly GrantLevelOption[];
  source?: GrantSource;
  /** "Removed 30 Sep by Sam Ode", for a removed grant. */
  removedText?: string;
  lock?: GrantLock;
  /** Whether the row's switch is drawn. A locked or not-granted row has none. */
  hasSwitch: boolean;
  /**
   * What the not-granted row's button says. Notifications are always held at need-to-know, so their
   * one-click grant widens rather than grants.
   */
  grantVerb?: "grant" | "widen";
}>;

export type GrantRowContext = Readonly<{
  kind: PermissionKind;
  /** Every kind, to name a derived grant's parents. */
  kinds: readonly PermissionKind[];
  /** The kind's live grant for this person, or the removed one when there is no live one. */
  permission?: Permission;
  viewerUserId: Guid;
  /** The viewer's `canAdministerPlatform()`: only they may choose or change Global scope. */
  mayAdministerPlatform: boolean;
  /**
   * Whether the directory named the grantor. A page that looks grantors up passes `loading` until the
   * directory answers. Leaving it out draws no grantor source at all: a template's preview and the
   * summary after applying one look nobody up, and a skeleton there would never resolve.
   */
  grantor?: NameState;
  /**
   * Whether the directory named who removed the grant, looked up as `grantor` is. Left out, a removed
   * row still says "by you" and "by the system", and leaves out a person it was not given the name of.
   */
  remover?: NameState;
  today?: AHDate;
  /**
   * The viewer's `canAccessAdministration()`: whether a template-made grant's source links to the
   * template page, which refuses anybody else. Left out, the source names the template unlinked — a
   * member reading their own grants, say.
   */
  mayOpenTemplates?: boolean;
}>;

const EMPTY_LEVEL: GrantLevel = {};

export function grantRowId(kind: PermissionKind, permission?: Permission): string {
  return permission?.id ? `grant-${permission.id.value}` : `grant-kind-${kind.slug}`;
}

/** The level and scope a resource holds, whichever of the two it has. */
export function resourceLevel(resource: Resource): GrantLevel {
  const { accessLevel, scope } = resource as Resource & { accessLevel?: AccessLevel; scope?: Scope };
  return { accessLevel: accessLevel ?? undefined, scope: scope ?? undefined };
}

/** The kind a resource is an instance of, read the way the server's `PermissionAreas.Find` reads it. */
export function kindOfResource(kinds: readonly PermissionKind[], resource: Resource): PermissionKind | undefined {
  const properties = resource as unknown as Record<string, unknown>;
  return kinds.find(
    (kind) =>
      kind.resourceType === resource.resourceType &&
      (kind.discriminator === null || properties[kind.discriminator.property] === kind.discriminator.value),
  );
}

function controlValues(kind: PermissionKind, control: "accessLevel" | "scope", mayAdministerPlatform: boolean): number[] | undefined {
  const found = kind.controls.find((candidate) => candidate.name === control);
  return found?.options.filter((option) => mayAdministerPlatform || !option.superUserOnly).map((option) => option.value);
}

function isInherentNotifications(kind: PermissionKind): boolean {
  return kind.resourceType === ResourceType.NotificationsAccess;
}

/**
 * Whether the kind is the person's own sign-in: the one grant whose row locks for its own reason, and
 * the one "Remove all access…" keeps unless "Also stop sign-in" is ticked. The one definition of it.
 */
export function isOwnSignIn(kind: PermissionKind): boolean {
  return kind.resourceType === ResourceType.LoginAccess && kind.discriminator?.value === LoginAccessType.Self;
}

/**
 * The (level, scope) pairs the kind's controls allow, in the order a reader compares them: by scope,
 * then by level. Global appears only for a platform administrator; a grant already at Global keeps
 * Global for everyone else and offers only its level. Notifications never offer read-only at
 * need-to-know, which the server refuses because everybody already holds read and write there.
 */
export function levelOptions(kind: PermissionKind, current: GrantLevel, mayAdministerPlatform: boolean): GrantLevelOption[] {
  const levels = controlValues(kind, "accessLevel", mayAdministerPlatform);
  let scopes = controlValues(kind, "scope", mayAdministerPlatform);
  if (scopes && current.scope === Scope.Global && !mayAdministerPlatform) scopes = [Scope.Global];

  const pairs: GrantLevel[] = [];
  for (const scope of scopes ?? [undefined]) {
    for (const accessLevel of levels ?? [undefined]) {
      pairs.push({ accessLevel: accessLevel as AccessLevel | undefined, scope: scope as Scope | undefined });
    }
  }

  return pairs
    .filter((level) => level.accessLevel !== undefined || level.scope !== undefined)
    .filter(
      (level) =>
        !isInherentNotifications(kind) ||
        !(level.scope === Scope["Need-to-Know"] && level.accessLevel === AccessLevel["Read-Only"]),
    )
    .map((level) => ({ level, label: levelLabel(level), help: levelHelp(level) }));
}

function levelControl(kind: PermissionKind): "accessLevel" | "scope" | undefined {
  if (kind.controls.some((control) => control.name === "accessLevel")) return "accessLevel";
  if (kind.controls.some((control) => control.name === "scope")) return "scope";
  return undefined;
}

function defaultLevel(kind: PermissionKind): GrantLevel {
  return {
    accessLevel: (kind.default?.accessLevel ?? undefined) as AccessLevel | undefined,
    scope: (kind.default?.scope ?? undefined) as Scope | undefined,
  };
}

function isEmptyGrantor(grantorUserId: Guid): boolean {
  return grantorUserId.isEqualTo(Guid.EMPTY);
}

/**
 * The source of a grant a template made: `From template "Paralegal"`, linked to the template when the
 * viewer may open it. An administrator with Member Permissions may open every template of the
 * organization (#6826); a member reading their own grants may not, and a deleted template is gone.
 * Either way the name stays and the link goes.
 */
export function templateSource(source: NonNullable<Permission["fromTemplate"]>, mayOpenTemplates: boolean): GrantSource {
  const label = fromTemplate(source.name);
  if (source.isDeleted || !mayOpenTemplates) return { type: "template", label };
  return { type: "template", label, href: templatePath(source.templateId.value) };
}

/**
 * Where a grant came from: a person, the system, the parents it was derived from, or the roster of a
 * proposal or work agreement (a forum grant whose note starts "Derived forum access:").
 */
export function grantSource(
  permission: Permission,
  kinds: readonly PermissionKind[],
  grantor: NameState | undefined,
  today: AHDate,
  mayOpenTemplates = false,
): GrantSource | undefined {
  // Documents decide who may open a document without reading a grant's parent (design decision 3),
  // so the link is no source at all; the row says so and keeps its Remove.
  if (permission.resource.resourceType === ResourceType.DocumentAccess && (permission.parentPermissions?.length ?? 0) > 0) {
    return { type: "legacy", label: LEGACY_LINK, explanation: LEGACY_LINK_EXPLANATION };
  }
  if (permission.parentPermissions && permission.parentPermissions.length > 0) {
    return {
      type: "parents",
      parents: permission.parentPermissions.map((parent) => {
        const parentKind = kindOfResource(kinds, parent.resource);
        return {
          label: parentKind ? parentLabel(parentKind, resourceLevel(parent.resource).scope) : UNRECOGNIZED_PARENT,
          rowId: parentKind ? grantRowId(parentKind, parent) : undefined,
        };
      }),
    };
  }
  if (permission.note?.startsWith(DERIVED_FORUM_NOTE_PREFIX)) {
    return { type: "roster", label: derivedForumSource(permission.note) };
  }
  if (permission.fromTemplate) return templateSource(permission.fromTemplate, mayOpenTemplates);
  if (isEmptyGrantor(permission.grantorUserId)) {
    return { type: "system", label: BY_THE_SYSTEM, note: permission.note || undefined };
  }
  if (!grantor) return undefined;
  if (grantor.state === "found") return { type: "grantor", label: grantedBy(grantor.name, permission.createdDate, today) };
  return { type: "unnamed-grantor", state: grantor.state, label: grantedOn(permission.createdDate, today), stateText: personStateText(grantor) };
}

/**
 * Who a person id names in a grant's history: the reader, a person the directory named, or nobody
 * this page can name. The system is an empty grantor or a null remover, and the caller decides that.
 */
export function personActor(userId: Guid, viewerUserId: Guid, name: NameState | undefined): GrantActor | undefined {
  if (userId.isEqualTo(viewerUserId)) return { type: "viewer" };
  return name?.state === "found" ? { type: "person", name: name.name } : undefined;
}

/**
 * Who removed a grant (#6825). A null remover is the system — or a removal from before #6824, which
 * recorded nobody, and which the server cannot tell apart. An undefined one is a server that did not
 * say, and the row names nobody.
 */
export function removerOf(permission: Permission, viewerUserId: Guid, remover: NameState | undefined): GrantActor | undefined {
  if (permission.disabledByUserId === null) return { type: "system" };
  if (permission.disabledByUserId === undefined) return undefined;
  return personActor(permission.disabledByUserId, viewerUserId, remover);
}

/**
 * The people a grant's row names, for a page to look up: its grantor unless the system granted it, and
 * whoever removed it (#6825).
 */
export function grantPeopleIds(grant: Permission): string[] {
  const ids: string[] = [];
  if (!isEmptyGrantor(grant.grantorUserId)) ids.push(grant.grantorUserId.value);
  if (grant.disabledByUserId) ids.push(grant.disabledByUserId.value);
  return ids;
}

/** The parents of a derived grant, joined the way its lock explanation names them. */
function parentNames(source: GrantSource | undefined): string | undefined {
  if (source?.type === "parents") return source.parents.map((parent) => parent.label).join(" + ");
  if (source?.type === "roster") return source.label.slice(FROM.length).trim();
  return undefined;
}

function lockReason(context: GrantRowContext, permission: Permission | undefined, source: GrantSource | undefined, level: GrantLevel): GrantLockReason | undefined {
  const { kind, viewerUserId, mayAdministerPlatform } = context;
  if (source?.type === "parents" || source?.type === "roster") return "derived";
  if (permission && permission.granteeUserId.isEqualTo(viewerUserId)) return "own";
  if (isOwnSignIn(kind)) return "own-sign-in";
  if (kind.superUserOnly) return "platform";
  if (isInherentNotifications(kind) && !permission?.isDisabled) return "inherent";
  if (level.scope === Scope.Global && !mayAdministerPlatform) return "global";
  return undefined;
}

/**
 * Whether a lock leaves the row's controls live. Global scope fixes only the scope, and inherent
 * notifications can still be widened or narrowed; everything else is read-only.
 */
function lockKeepsLevel(reason: GrantLockReason | undefined): boolean {
  return reason === undefined || reason === "global" || reason === "inherent";
}

function lockKeepsSwitch(reason: GrantLockReason | undefined): boolean {
  return reason === undefined || reason === "global";
}

export function buildGrantRow(context: GrantRowContext): GrantRowModel {
  const { kind, kinds, permission, mayAdministerPlatform, grantor } = context;
  const today = context.today ?? AHDate.TODAY;
  const rowId = grantRowId(kind, permission);

  if (!permission) {
    // Everybody holds notifications at need-to-know, read and write, so "not granted" is never
    // true of them: the row says what is held and offers the organization-wide grant.
    if (isInherentNotifications(kind)) {
      const inherent: GrantLevel = { accessLevel: AccessLevel["Read/Write"], scope: Scope["Need-to-Know"] };
      return {
        rowId,
        kind,
        state: "not-granted",
        level: inherent,
        levelText: levelLabel(inherent),
        levelControl: levelControl(kind),
        levelOptions: [],
        lock: { reason: "inherent", ...lockWording("inherent") },
        hasSwitch: false,
        grantVerb: kind.grantable ? "widen" : undefined,
      };
    }
    return {
      rowId,
      kind,
      state: "not-granted",
      level: defaultLevel(kind),
      levelText: NOT_GRANTED,
      levelControl: levelControl(kind),
      levelOptions: [],
      hasSwitch: false,
      // A kind with no default — one proposal, one work agreement — needs its fields chosen first, so a
      // one-click Grant would send nothing meaningful; the field editor is the way in.
      grantVerb: kind.grantable && !kind.retired && kind.default !== null ? "grant" : undefined,
    };
  }

  const level = kind.controls.length === 0 ? EMPTY_LEVEL : resourceLevel(permission.resource);
  const source = grantSource(permission, kinds, grantor, today, context.mayOpenTemplates ?? false);
  const reason = lockReason(context, permission, source, level);
  const lock = reason ? { reason, ...lockWording(reason, parentNames(source)) } : undefined;
  const removed = permission.isDisabled;

  return {
    rowId,
    kind,
    state: removed ? "removed" : "enabled",
    level,
    levelText: levelLabel(level),
    levelControl: levelControl(kind),
    // A removed grant's level is read-only: changing it would edit a row nobody holds.
    levelOptions: !removed && lockKeepsLevel(reason) ? levelOptions(kind, level, mayAdministerPlatform) : [],
    source,
    removedText: removed ? removedOn(permission.disabledDate, today, removerOf(permission, context.viewerUserId, context.remover)) : undefined,
    lock,
    hasSwitch: lockKeepsSwitch(reason) && !kind.retired,
  };
}
